/**
 * Plan first (pedagogy spec §4.3.3, B2; D2): before calculating, the student names
 * the principle the problem is mainly about with one tap on one of five chips.
 * The chips are buttons in a group, not radios, because the arrow keys would
 * commit a pick. The pick is final and never changes the score; the feedback
 * names the target with its cue and, after a wrong pick, the pick with its cue.
 * `PlanGate` keeps the answer controls disabled until the pick.
 */
import clsx from "clsx";
import { forwardRef, useId, useImperativeHandle, useRef, type ReactNode } from "react";
import { useLocalizedText } from "@client/components/Localized";
import { useT } from "@client/i18n";
import type { PlanOffer } from "./types";

export interface PlanStepHandle {
  /** Moves focus to the picked chip, else the plan group (a pending pick takes Enter, S and H). */
  focus(): void;
}

export interface PlanStepProps {
  offer: PlanOffer;
  /** The picked anchor (`QuestionResult.planPick`); undefined until the one tap. */
  pick: string | undefined;
  onPick: (topicId: string) => void;
  /**
   * The line under the question. The "Vilken princip?" set (step 2.8c) has no answer to score or to
   * count as help, so it says its own; the default is the plan-first note.
   */
  note?: string;
}

export const PlanStep = forwardRef<PlanStepHandle, PlanStepProps>(function PlanStep(
  { offer, pick, onPick, note },
  ref,
) {
  const t = useT();
  const localize = useLocalizedText();
  const titleId = useId();
  const noteId = useId();
  const groupRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      focus() {
        const chips = groupRef.current?.querySelectorAll<HTMLButtonElement>("button");
        const pressed = chips
          ? Array.from(chips).find((chip) => chip.getAttribute("aria-pressed") === "true")
          : undefined;
        // A pending pick moves focus to the group, not the first chip (spec
        // §4.3.10): a reflexive second Enter would otherwise activate that chip
        // as a button and commit it (ruling 2026-09-28, practice-client-13).
        (pressed ?? groupRef.current)?.focus();
      },
    }),
    [],
  );

  const picked = pick !== undefined;
  const target = offer.options.find((option) => option.topicId === offer.targetTopicId);
  const chosen = offer.options.find((option) => option.topicId === pick);

  return (
    <div className="surface mt-4 p-4">
      <p id={titleId} className="text-sm font-semibold text-ink-900 dark:text-ink-50">
        {t("practice.plan.legend")}
      </p>
      <p id={noteId} className="mt-1 text-xs text-ink-600 dark:text-ink-400">
        {note ?? t("practice.plan.note")}
      </p>
      <div
        ref={groupRef}
        role="group"
        tabIndex={-1}
        aria-labelledby={titleId}
        aria-describedby={noteId}
        className="mt-3 flex flex-wrap gap-2"
      >
        {offer.options.map((option) => {
          const state = !picked
            ? undefined
            : option.topicId === offer.targetTopicId
              ? "target"
              : option.topicId === pick
                ? "missed"
                : undefined;
          return (
            <button
              key={option.topicId}
              type="button"
              aria-pressed={option.topicId === pick}
              aria-disabled={picked || undefined}
              data-plan={state}
              onClick={() => {
                if (!picked) onPick(option.topicId);
              }}
              className={clsx(
                "rounded-full border px-3 py-1.5 text-sm first-letter:uppercase",
                !picked &&
                  "border-ink-300 text-ink-800 hover:bg-ink-100 dark:border-ink-700 dark:text-ink-100 dark:hover:bg-ink-800",
                state === "target" &&
                  "border-green-600 bg-green-50 text-green-900 dark:border-green-500 dark:bg-green-950/60 dark:text-green-100",
                state === "missed" &&
                  "border-amber-500 bg-amber-50 text-amber-900 dark:border-amber-400 dark:bg-amber-950/60 dark:text-amber-100",
                picked &&
                  !state &&
                  "border-ink-200 text-ink-500 dark:border-ink-800 dark:text-ink-400",
                picked && "cursor-default",
              )}
            >
              {localize(option.name)}
            </button>
          );
        })}
      </div>
      <div role="status" className="mt-3 space-y-1 text-sm">
        {picked &&
          target &&
          (pick === offer.targetTopicId ? (
            <p className="font-medium text-green-800 dark:text-green-200">
              {t("practice.plan.right", { principle: localize(target.name) })}
            </p>
          ) : (
            <>
              <p className="font-medium text-ink-900 dark:text-ink-50">
                {t("practice.plan.wrong", { principle: localize(target.name) })}
              </p>
              <p className="text-ink-700 dark:text-ink-300">{localize(target.cue)}</p>
              {chosen && (
                <>
                  <p className="pt-2 font-medium text-ink-900 dark:text-ink-50">
                    {t("practice.plan.yourPick", { principle: localize(chosen.name) })}
                  </p>
                  <p className="text-ink-700 dark:text-ink-300">{localize(chosen.cue)}</p>
                </>
              )}
            </>
          ))}
      </div>
    </div>
  );
});

export interface PlanGateProps {
  /** The question has a plan step. */
  active: boolean;
  /** The pick has not been made yet. */
  locked: boolean;
  children: ReactNode;
}

/**
 * Until the pick, the answer controls are disabled with their reason (pedagogy
 * spec §4.3.3): a `fieldset` disables every input and button inside it, so the
 * answer, the rating, "Svara", "Klar" and "Ge upp" all wait. Without a plan step
 * the children render untouched.
 */
export function PlanGate({ active, locked, children }: PlanGateProps) {
  const t = useT();
  const noteId = useId();
  if (!active) return <>{children}</>;
  return (
    <>
      {locked && (
        <p id={noteId} className="mt-4 text-sm text-ink-600 dark:text-ink-400">
          {t("practice.plan.chooseFirst")}
        </p>
      )}
      <fieldset
        disabled={locked}
        aria-describedby={locked ? noteId : undefined}
        className="m-0 min-w-0 border-0 p-0"
      >
        {children}
      </fieldset>
    </>
  );
}
